/**
 * 배포 스큐·권한 스테일 응답 헤더 3개. **이름의 원본이 여기 하나다.**
 * (docs/decisions/0008 §should-fix-now · AGENTS.md 19)
 *
 * ─── 왜 커널이 아니라 contracts 인가 ───
 * 처음엔 `packages/rpc-kernel/src/http.ts` 에 있었다(2026-09-02, §구현 순서 8).
 * 헤더를 **읽는** 쪽이 생기면서 그 자리가 못 쓰게 됐다: 읽는 쪽은 브라우저이고,
 * 커널은 `@orpc/server` 와 postgres 를 물고 있어 번들에 들어갈 수 없다.
 * 브라우저가 이름을 다시 적으면 그건 같은 사실의 사본 둘이고, 오타 난 헤더는
 * 에러가 아니라 **없는 헤더**이며, 없는 헤더는 "스큐 없음"으로 읽힌다 —
 * 조용히 틀리는 방향이다. `RPC_PREFIX` 를 여기로 올린 것과 **같은 논거·같은 자리**다.
 * 커널은 이 값을 import 해서 쓸 뿐 자기 사본을 두지 않는다.
 *
 * ─── 셋을 함께 내보낸다 ───
 * 둘만 나가면 클라이언트가 스테일을 *부분적으로* 판정하고, 그 상태는 "판정했다"와
 * 구분되지 않는다. 본문 봉투(`{success, data}`)는 건드리지 않는다 — 스테일 판정 하나
 * 때문에 모든 procedure 의 응답 타입을 바꾸는 것은 비례가 안 맞는다.
 *
 * **스캔이 못 보는 사본이 하나 있다**: `scripts/smoke-api.sh` 는 셸이라 import 를 못 한다.
 * 그건 진짜 소켓을 때리는 계약의 반대편이라 어긋나면 스모크가 즉시 빨개진다(조용하지 않다) —
 * `RPC_PREFIX` 와 같은 예외이고 같은 이유로 감수한다.
 */
export const STAMP_HEADERS = {
  /** 프로세스 고정. 권한 카탈로그 + 평가 코드의 지문(`evalHashOf`). */
  evalHash: 'x-wr-eval-hash',
  /** 프로세스 고정. 표면 선언의 지문(`surfaceFingerprint` → sha256). */
  surfaceHash: 'x-wr-surface-hash',
  /** 요청마다 다름. `<cacheEpoch>.<rbacVersion>` — 인증된 요청에만 붙는다. */
  rbacVersion: 'x-wr-rbac-version',
} as const;

/**
 * 응답에서 읽어 낸 스탬프. `rbacVersion` 이 `undefined` 인 것은 **미인증 응답**이다 —
 * 빈 문자열로 채우지 않는 이유가 서버 쪽과 같다: `''` 와 진짜 버전을 비교하기 시작하면
 * 매 요청이 스테일로 보인다.
 */
export interface ObservedStamp {
  readonly evalHash: string | undefined;
  readonly surfaceHash: string | undefined;
  readonly rbacVersion: string | undefined;
}

/** 헤더 셋을 한 번에 읽는다. 이름을 쓰는 자리를 늘리지 않기 위해 읽는 쪽도 함수 하나다. */
export function readStamp(headers: { get(name: string): string | null }): ObservedStamp {
  return {
    evalHash: headers.get(STAMP_HEADERS.evalHash) ?? undefined,
    surfaceHash: headers.get(STAMP_HEADERS.surfaceHash) ?? undefined,
    rbacVersion: headers.get(STAMP_HEADERS.rbacVersion) ?? undefined,
  };
}
